import { useEffect, useMemo, useState } from 'react'
import type { TripItineraryData } from 'tripPlanner/api/types'
import {
  computeCartPanelTotal,
  isCartPanelItemCheckoutDataLoading,
  isCartPanelLinkedCheckoutProjectionPending,
  shouldIncludeCartPanelItemPriceEstimate,
} from '../utils/cartPanelUtils'
import { useCartPanelItems, type CartPanelItem } from './useCartPanelItems'

const EMPTY_OPTIMISTIC_EXCLUSIONS: Record<number, boolean> = {}
const EMPTY_REFRESH_FAILED_FLIGHT_KEYS: Record<string, boolean> = {}

export interface CartButtonSummary {
  bookableItemCount: number
  estimatedTotal: number
  isLoading: boolean
}

interface StableCartButtonSummary extends CartButtonSummary {
  scope: string
  signature: string
}

function getCartButtonSummaryScope(itineraryData: TripItineraryData | undefined): string {
  return JSON.stringify({
    tripChatId: itineraryData?.tripChatId,
    multiCartId: itineraryData?.multiCartId,
  })
}

function getCartButtonSummarySignature(items: Array<CartPanelItem>): string {
  return JSON.stringify(items.map(item => ({
    key: item.key,
    productType: item.productType,
    source: item.source,
    cartStatus: item.cartStatus,
    cartExcluded: item.cartExcluded,
    includeInTotal: item.includeInTotal,
    cartItemId: item.cartItemId,
    cartItemState: item.cartItem?.state,
    cartItemPurchasedBy: item.cartItem?.purchasedBy?.userId,
    price: item.pricing.price,
    memberPrice: item.pricing.memberPrice,
  })))
}

function hasLinkedItineraryFallbackItem(items: Array<CartPanelItem>): boolean {
  return items.some(isCartPanelLinkedCheckoutProjectionPending)
}

function hasLoadingIncludedPrice(items: Array<CartPanelItem>): boolean {
  return items.some(item => item.includeInTotal && isCartPanelItemCheckoutDataLoading(item))
}

export function useCartButtonSummary(
  itineraryData: TripItineraryData | undefined,
  isLoggedIn: boolean,
  luxPlusEnabled: boolean,
): CartButtonSummary {
  const multiCartId = itineraryData?.multiCartId
  const {
    items,
    isLoading,
  } = useCartPanelItems({
    multiCartId,
    itineraryData,
    isLoggedIn,
    optimisticExclusions: EMPTY_OPTIMISTIC_EXCLUSIONS,
    refreshFailedFlightKeys: EMPTY_REFRESH_FAILED_FLIGHT_KEYS,
  })
  const scope = useMemo(
    () => getCartButtonSummaryScope(itineraryData),
    [itineraryData],
  )

  const signature = useMemo(
    () => getCartButtonSummarySignature(items),
    [items],
  )

  const currentSummary = useMemo((): CartButtonSummary => {
    const itemsForTotal = items.map(item => {
      return {
        cartExcluded: item.cartExcluded,
        price: item.pricing.price,
        memberPrice: item.pricing.memberPrice,
        includeInTotal: shouldIncludeCartPanelItemPriceEstimate(item),
        includeInCount: true,
      }
    })
    const { totalPrice, totalItemCount } = computeCartPanelTotal(
      itemsForTotal,
      luxPlusEnabled,
    )
    return {
      bookableItemCount: totalItemCount,
      estimatedTotal: totalPrice,
      isLoading: false,
    }
  }, [items, luxPlusEnabled])

  const shouldHoldPreviousSummary = isLoggedIn && !!multiCartId && (
    (isLoading && hasLinkedItineraryFallbackItem(items)) ||
    hasLoadingIncludedPrice(items)
  )
  const [stableSummary, setStableSummary] = useState<StableCartButtonSummary | undefined>()

  useEffect(() => {
    if (shouldHoldPreviousSummary) return

    setStableSummary(previous => {
      if (
        previous?.scope === scope &&
        previous.signature === signature &&
        previous.bookableItemCount === currentSummary.bookableItemCount &&
        previous.estimatedTotal === currentSummary.estimatedTotal
      ) {
        return previous
      }

      return {
        ...currentSummary,
        scope,
        signature,
      }
    })
  }, [currentSummary, scope, shouldHoldPreviousSummary, signature])

  if (!shouldHoldPreviousSummary) return currentSummary

  if (stableSummary?.scope === scope) {
    return {
      bookableItemCount: stableSummary.bookableItemCount,
      estimatedTotal: stableSummary.estimatedTotal,
      isLoading: false,
    }
  }

  return {
    bookableItemCount: 0,
    estimatedTotal: 0,
    isLoading: true,
  }
}
